import React, { useState, useEffect, useRef } from 'react';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DueTimeInputProps {
  value: string; // "HH:mm" or ""
  onChange: (value: string) => void;
  onConfirm: (confirmedValue: string) => void;
  disabled?: boolean;
  className?: string;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
const MINUTES = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));

export function DueTimeInput({ value, onChange, onConfirm, disabled, className }: DueTimeInputProps) {
  const [open, setOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState('08');
  const [selectedMinute, setSelectedMinute] = useState('00');
  const containerRef = useRef<HTMLDivElement>(null);
  const hourListRef = useRef<HTMLDivElement>(null);
  const minuteListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value && value.includes(':')) {
      const [h, m] = value.split(':');
      setSelectedHour(h || '08');
      const mNum = parseInt(m) || 0;
      const snapped = (Math.round(mNum / 5) * 5) % 60;
      setSelectedMinute(snapped.toString().padStart(2, '0'));
    }
  }, [value]);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        const hIdx = HOURS.indexOf(selectedHour);
        const mIdx = MINUTES.indexOf(selectedMinute);
        if (hourListRef.current && hIdx >= 0) {
          const el = hourListRef.current.children[hIdx] as HTMLElement;
          el?.scrollIntoView({ block: 'center', behavior: 'instant' });
        }
        if (minuteListRef.current && mIdx >= 0) {
          const el = minuteListRef.current.children[mIdx] as HTMLElement;
          el?.scrollIntoView({ block: 'center', behavior: 'instant' });
        }
      }, 0);
    }
  }, [open, selectedHour, selectedMinute]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleConfirm = () => {
    const val = `${selectedHour}:${selectedMinute}`;
    onChange(val);
    setOpen(false);
    setTimeout(() => onConfirm(val), 0);
  };

  const handleClear = () => {
    onChange('');
    setOpen(false);
    setTimeout(() => onConfirm(''), 0);
  };

  const displayValue = value && value !== '' ? value : null;

  return (
    <div ref={containerRef} className={`relative ${className || ''}`}>
      <style>{`.due-time-scroll::-webkit-scrollbar { display: none; }`}</style>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(!open)}
        className={cn(
          "h-8 px-2.5 text-sm rounded-lg bg-muted/50 shadow-sm transition-all inline-flex items-center gap-1.5",
          "hover:bg-muted hover:shadow focus:ring-2 focus:ring-ring focus:ring-offset-1 outline-none",
          "disabled:cursor-not-allowed disabled:opacity-50",
          displayValue ? "text-foreground font-medium" : "text-muted-foreground"
        )}
      >
        {displayValue || '--:--'}
      </button>

      {open && !disabled && (
        <div className="absolute top-full left-0 mt-1.5 z-50 bg-popover/95 backdrop-blur-xl border border-border/50 rounded-xl shadow-2xl overflow-hidden animate-scale-in">
          {/* Columns */}
          <div className="flex gap-0 p-1.5">
            {/* Hours */}
            <div
              ref={hourListRef}
              className="due-time-scroll h-44 w-14 overflow-y-auto flex flex-col gap-0.5 px-0.5"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {HOURS.map(h => (
                <button
                  key={h}
                  type="button"
                  className={cn(
                    "py-1.5 text-sm rounded-lg text-center transition-all shrink-0",
                    h === selectedHour
                      ? "bg-primary text-primary-foreground font-semibold shadow-sm"
                      : "text-foreground/70 hover:bg-muted hover:text-foreground"
                  )}
                  onClick={() => setSelectedHour(h)}
                >
                  {h}
                </button>
              ))}
            </div>

            <div className="flex items-center px-0.5">
              <span className="text-muted-foreground/50 font-bold text-lg">:</span>
            </div>

            {/* Minutes */}
            <div
              ref={minuteListRef}
              className="due-time-scroll h-44 w-14 overflow-y-auto flex flex-col gap-0.5 px-0.5"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {MINUTES.map(m => (
                <button
                  key={m}
                  type="button"
                  className={cn(
                    "py-1.5 text-sm rounded-lg text-center transition-all shrink-0",
                    m === selectedMinute
                      ? "bg-primary text-primary-foreground font-semibold shadow-sm"
                      : "text-foreground/70 hover:bg-muted hover:text-foreground"
                  )}
                  onClick={() => setSelectedMinute(m)}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          {/* Preview + Actions bar */}
          <div className="border-t border-border/30 px-2 py-1.5 flex items-center justify-between bg-muted/30">
            <span className="text-sm font-mono font-semibold text-foreground/80 pl-1">
              {selectedHour}:{selectedMinute}
            </span>
            <div className="flex items-center gap-1">
              {displayValue && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="h-7 w-7 flex items-center justify-center rounded-lg text-destructive/60 bg-destructive/5 hover:text-destructive hover:bg-destructive/15 transition-colors"
                  title="Supprimer l'heure"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={handleConfirm}
                className="h-7 px-2.5 flex items-center justify-center gap-1 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-xs font-medium"
                title="Confirmer"
              >
                <Check className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
